import { useEffect, useState } from 'react';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import Breadcrumb from '../../components/Breadcrumb';

export default function FAQPage() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  useEffect(() => {
    // Page title
    document.title = 'Insulation FAQ Yuma AZ | Common Questions Answered';

    // Meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute(
        'content',
        'Spray foam insulation FAQ for Yuma, AZ. Get answers about costs, installation, R-value, and energy savings. Expert advice. Call (928) 427-8140.'
      );
    }

    // Canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', window.location.origin + '/yuma-insulation-faq');

    // Geo-location tags
    const metaTags = [
      { name: 'geo.region', content: 'US-AZ' },
      { name: 'geo.placename', content: 'Yuma' },
      { name: 'geo.position', content: '32.6927;-114.6277' },
      { name: 'ICBM', content: '32.6927, -114.6277' },
    ];
    metaTags.forEach(tag => {
      let el = document.querySelector(`meta[name="${tag.name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('name', tag.name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', tag.content);
    });

    // Open Graph tags
    const ogTags = [
      { property: 'og:title', content: 'Insulation FAQ Yuma AZ | Common Questions Answered' },
      {
        property: 'og:description',
        content: 'Spray foam insulation FAQ for Yuma, AZ. Get answers about costs, installation, and energy savings.'
      },
      { property: 'og:type', content: 'website' },
      { property: 'og:url', content: window.location.origin + '/yuma-insulation-faq' },
      {
        property: 'og:image',
        content: 'https://public.readdy.ai/ai/img_res/0bbb2a4e0ed5d3ca88eeda4cc0e46b21.jpg'
      }
    ];
    ogTags.forEach(tag => {
      let el = document.querySelector(`meta[property="${tag.property}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('property', tag.property);
        document.head.appendChild(el);
      }
      el.setAttribute('content', tag.content);
    });

    // Twitter Card tags
    const twitterTags = [
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: 'Insulation FAQ Yuma AZ | Common Questions Answered' },
      {
        name: 'twitter:description',
        content: 'Get answers about spray foam insulation costs, installation, and energy savings in Yuma, AZ.'
      },
      {
        name: 'twitter:image',
        content: 'https://readdy.ai/api/search-image?query=Clean%20modern%20FAQ%20help%20center%20interface%20with%20organized%20question%20and%20answer%20layout%20professional%20design%20emphasizing%20clarity%20and%20helpful%20information%20for%20customers%20seeking%20insulation%20guidance&width=1200&height=630&seq=twitter-faq-001&orientation=landscape'
      }
    ];
    twitterTags.forEach(tag => {
      let el = document.querySelector(`meta[name="${tag.name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('name', tag.name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', tag.content);
    });

    // Last-modified tag
    let lastModified = document.querySelector('meta[name="last-modified"]');
    if (!lastModified) {
      lastModified = document.createElement('meta');
      lastModified.setAttribute('name', 'last-modified');
      document.head.appendChild(lastModified);
    }
    lastModified.setAttribute('content', new Date().toISOString().split('T')[0]);

    // Breadcrumb schema
    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: window.location.origin + '/'
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'FAQ',
          item: window.location.origin + '/yuma-insulation-faq'
        }
      ]
    };

    let breadcrumbScript = document.querySelector('script[data-schema="breadcrumb-faq"]');
    if (!breadcrumbScript) {
      breadcrumbScript = document.createElement('script');
      breadcrumbScript.setAttribute('type', 'application/ld+json');
      breadcrumbScript.setAttribute('data-schema', 'breadcrumb-faq');
      document.head.appendChild(breadcrumbScript);
    }
    breadcrumbScript.textContent = JSON.stringify(breadcrumbSchema);

    // FAQPage schema
    const faqSchema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'How much does spray foam insulation cost in Yuma, AZ?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Spray foam insulation costs vary based on project size and foam type. Open cell foam typically ranges from $1.00-$1.50 per board foot, while closed cell foam ranges from $1.50-$3.00 per board foot. Most residential projects in Yuma range from $2,000-$8,000. We offer free quotes to provide accurate pricing for your specific needs.'
          }
        },
        {
          '@type': 'Question',
          name: 'What is the R-value of spray foam insulation?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Closed cell spray foam provides an R-value of approximately R-6 to R-7 per inch, making it one of the highest R-value insulation materials available. Open cell spray foam provides approximately R-3.5 to R-4 per inch. For Yuma\'s desert climate, we typically recommend closed cell foam for maximum energy efficiency and moisture protection.'
          }
        },
        {
          '@type': 'Question',
          name: 'How long does spray foam insulation last?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Spray foam insulation can last 80-100 years or more when properly installed. Unlike traditional insulation that can settle, compress, or deteriorate over time, spray foam maintains its R-value and structural integrity throughout its lifespan. This makes it an excellent long-term investment for Arizona homeowners and businesses.'
          }
        },
        {
          '@type': 'Question',
          name: 'Is spray foam insulation safe?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes, spray foam insulation is completely safe once cured. During installation, we follow strict safety protocols and the area must be ventilated. After 24 hours of curing, the foam is inert and safe for occupancy. Spray foam is non-toxic, does not support mold growth, and improves indoor air quality by sealing out allergens and pollutants.'
          }
        },
        {
          '@type': 'Question',
          name: 'How much can I save on energy bills with spray foam insulation?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Homeowners and businesses in Yuma typically see energy cost reductions of 30-50% after spray foam installation. In Arizona\'s extreme desert climate, proper insulation is crucial for managing cooling costs. Many customers report their spray foam investment pays for itself within 3-7 years through energy savings alone.'
          }
        }
      ]
    };

    let faqScript = document.querySelector('script[data-schema="faq"]');
    if (!faqScript) {
      faqScript = document.createElement('script');
      faqScript.setAttribute('type', 'application/ld+json');
      faqScript.setAttribute('data-schema', 'faq');
      document.head.appendChild(faqScript);
    }
    faqScript.textContent = JSON.stringify(faqSchema);
  }, []);

  const faqs = [
    {
      question: 'How much does spray foam insulation cost in Yuma, AZ?',
      answer:
        'Spray foam insulation costs vary based on project size and foam type. <a href="/open-cell-spray-foam" class="text-blue-600 hover:text-blue-700 font-semibold underline cursor-pointer">Open cell foam</a> typically ranges from $1.00-$1.50 per board foot, while <strong>closed cell foam</strong> ranges from $1.50-$3.00 per board foot. Most residential projects in Yuma range from $2,000-$8,000. We offer free quotes to provide accurate pricing for your specific needs.'
    },
    {
      question: 'What is the R-value of spray foam insulation?',
      answer:
        '<strong>Closed cell spray foam</strong> provides an R-value of approximately <strong>R-6 to R-7 per inch</strong>, making it one of the highest R-value insulation materials available. Open cell spray foam provides approximately R-3.5 to R-4 per inch. For Yuma\'s desert climate, we typically recommend <a href="/closed-cell-spray-foam" class="text-blue-600 hover:text-blue-700 font-semibold underline cursor-pointer">closed cell foam</a> for maximum <strong>energy efficiency</strong> and moisture protection.'
    },
    {
      question: 'How long does spray foam insulation last?',
      answer:
        'Spray foam insulation can last <strong>80-100 years or more</strong> when properly installed. Unlike traditional insulation that can settle, compress, or deteriorate over time, spray foam maintains its R-value and structural integrity throughout its lifespan. This makes it an excellent long-term investment for <a href="/residential-insulation-yuma" class="text-blue-600 hover:text-blue-700 font-semibold underline cursor-pointer">Arizona homeowners</a> and businesses.'
    },
    {
      question: 'Is spray foam insulation safe?',
      answer:
        'Yes, spray foam insulation is completely safe once cured. During installation, we follow strict safety protocols and the area must be ventilated. After 24 hours of curing, the foam is inert and safe for occupancy. Spray foam is <strong>non-toxic</strong>, does not support mold growth, and improves indoor air quality by sealing out allergens and pollutants.'
    },
    {
      question: 'How much can I save on energy bills with spray foam insulation?',
      answer:
        'Homeowners and businesses in Yuma typically see <strong>energy cost reductions of 30-50%</strong> after spray foam installation. In Arizona\'s extreme desert climate, proper insulation is crucial for managing cooling costs. Many customers report their spray foam investment pays for itself within 3-7 years through <strong>energy savings</strong> alone.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <Breadcrumb items={[{ label: 'Home', href: '/' }, { label: 'FAQ' }]} />

      {/* Hero Section */}
      <section className="pt-32 pb-12 bg-gradient-to-br from-blue-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Spray Foam Insulation <strong>FAQ Yuma AZ</strong>
          </h1>
          <p className="text-xl text-gray-600">
            Get answers to common questions about spray foam insulation, costs, installation, and <strong>energy savings</strong> in Arizona's desert climate.
          </p>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">
            Common Questions About Spray Foam Insulation
          </h2>

          {/* Mobile: Horizontal Scroll */}
          <div className="md:hidden relative">
            <div className="overflow-x-auto pb-4 -mx-4 px-4 scrollbar-hide">
              <div className="flex gap-6" style={{ width: 'max-content' }}>
                {faqs.map((item, index) => (
                  <div key={index} className="bg-white p-6 rounded-xl shadow-md border border-gray-100" style={{ width: '85vw', flexShrink: 0 }}>
                    <h3 className="text-xl font-semibold text-blue-600 mb-3">{item.question}</h3>
                    <p className="text-gray-700 leading-relaxed" dangerouslySetInnerHTML={{ __html: item.answer }}></p>
                  </div>
                ))}
              </div>
            </div>
            <div className="text-center text-sm text-gray-500 mt-2">
              Swipe to see more FAQs →
            </div>
          </div>

          {/* Desktop: Vertical Stack */}
          <div className="hidden md:block space-y-8">
            {faqs.map((item, index) => (
              <div key={index} className="bg-white p-6 rounded-xl shadow-md border border-gray-100 hover:shadow-lg transition-shadow">
                <h3 className="text-xl font-semibold text-blue-600 mb-3">{item.question}</h3>
                <p className="text-gray-700 leading-relaxed" dangerouslySetInnerHTML={{ __html: item.answer }}></p>
              </div>
            ))}
          </div>

          {/* Additional Resources Section */}
          <div className="mt-16 bg-gradient-to-br from-blue-50 to-emerald-50 p-8 rounded-xl">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
              Still Have Questions?
            </h3>
            <p className="text-gray-600 text-center mb-8">
              Explore our comprehensive resources to learn more about spray foam insulation options and see real results from our work. We serve both residential and commercial clients throughout the region.
            </p>
            
            {/* Mobile: Horizontal Scroll */}
            <div className="md:hidden relative">
              <div className="overflow-x-auto pb-4 -mx-4 px-4 scrollbar-hide">
                <div className="flex gap-6" style={{ width: 'max-content' }}>
                  <a 
                    href="/closed-cell-spray-foam" 
                    className="block bg-white p-6 rounded-xl hover:bg-blue-50 transition-colors cursor-pointer group text-center"
                    style={{ width: '280px', flexShrink: 0 }}
                  >
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-blue-600 transition-colors">
                      <i className="ri-drop-line text-2xl text-blue-600 group-hover:text-white"></i>
                    </div>
                    <h4 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-blue-600">
                      Closed Cell Foam
                    </h4>
                    <p className="text-gray-600 text-sm">
                      Learn about premium R-7 insulation
                    </p>
                  </a>
                  <a 
                    href="/open-cell-spray-foam" 
                    className="block bg-white p-6 rounded-xl hover:bg-blue-50 transition-colors cursor-pointer group text-center"
                    style={{ width: '280px', flexShrink: 0 }}
                  >
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-blue-600 transition-colors">
                      <i className="ri-bubble-chart-line text-2xl text-blue-600 group-hover:text-white"></i>
                    </div>
                    <h4 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-blue-600">
                      Open Cell Foam
                    </h4>
                    <p className="text-gray-600 text-sm">
                      Discover affordable soundproofing
                    </p>
                  </a>
                  <a 
                    href="/about-yuma-insulation" 
                    className="block bg-white p-6 rounded-xl hover:bg-blue-50 transition-colors cursor-pointer group text-center"
                    style={{ width: '280px', flexShrink: 0 }}
                  >
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-blue-600 transition-colors">
                      <i className="ri-team-line text-2xl text-blue-600 group-hover:text-white"></i>
                    </div>
                    <h4 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-blue-600">
                      About Our Company
                    </h4>
                    <p className="text-gray-600 text-sm">
                      20+ years of experience
                    </p>
                  </a>
                </div>
              </div>
              <div className="text-center text-sm text-gray-500 mt-2">
                Swipe to see more →
              </div>
            </div>

            {/* Desktop: Grid */}
            <div className="hidden md:grid md:grid-cols-3 gap-6">
              <a 
                href="/closed-cell-spray-foam" 
                className="block bg-white p-6 rounded-xl hover:bg-blue-50 transition-colors cursor-pointer group text-center"
              >
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-blue-600 transition-colors">
                  <i className="ri-drop-line text-2xl text-blue-600 group-hover:text-white"></i>
                </div>
                <h4 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-blue-600">
                  Closed Cell Foam
                </h4>
                <p className="text-gray-600 text-sm">
                  Learn about premium R-7 insulation
                </p>
              </a>
              <a 
                href="/open-cell-spray-foam" 
                className="block bg-white p-6 rounded-xl hover:bg-blue-50 transition-colors cursor-pointer group text-center"
              >
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-blue-600 transition-colors">
                  <i className="ri-bubble-chart-line text-2xl text-blue-600 group-hover:text-white"></i>
                </div>
                <h4 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-blue-600">
                  Open Cell Foam
                </h4>
                <p className="text-gray-600 text-sm">
                  Discover affordable soundproofing
                </p>
              </a>
              <a 
                href="/about-yuma-insulation" 
                className="block bg-white p-6 rounded-xl hover:bg-blue-50 transition-colors cursor-pointer group text-center"
              >
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-blue-600 transition-colors">
                  <i className="ri-team-line text-2xl text-blue-600 group-hover:text-white"></i>
                </div>
                <h4 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-blue-600">
                  About Our Company
                </h4>
                <p className="text-gray-600 text-sm">
                  20+ years of experience
                </p>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Related Services Section */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Explore Our Services
            </h2>
            <p className="text-lg text-gray-600">
              Learn more about our insulation solutions
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <a 
              href="/closed-cell-spray-foam" 
              className="block bg-gray-50 p-6 rounded-xl hover:bg-blue-50 transition-colors cursor-pointer group"
            >
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4 group-hover:bg-blue-900 transition-colors">
                <i className="ri-drop-line text-2xl text-blue-900 group-hover:text-white"></i>
              </div>
              <h4 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-blue-900">
                Closed Cell Foam
              </h4>
              <p className="text-gray-600 text-sm">
                Premium moisture barrier
              </p>
            </a>
            <a 
              href="/open-cell-spray-foam" 
              className="block bg-gray-50 p-6 rounded-xl hover:bg-blue-50 transition-colors cursor-pointer group"
            >
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4 group-hover:bg-blue-900 transition-colors">
                <i className="ri-bubble-chart-line text-2xl text-blue-900 group-hover:text-white"></i>
              </div>
              <h4 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-blue-900">
                Open Cell Foam
              </h4>
              <p className="text-gray-600 text-sm">
                Affordable soundproofing
              </p>
            </a>
            <a 
              href="/yuma-projects" 
              className="block bg-gray-50 p-6 rounded-xl hover:bg-blue-50 transition-colors cursor-pointer group"
            >
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4 group-hover:bg-blue-900 transition-colors">
                <i className="ri-image-line text-2xl text-blue-900 group-hover:text-white"></i>
              </div>
              <h4 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-blue-900">
                View Projects
              </h4>
              <p className="text-gray-600 text-sm">
                See completed work
              </p>
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}